// One workload's settings — its documents (02-DESIGN/06-configuration/01-the-configuration-service.md
// § the console: Configuration; 01-the-console.md § the words: *settings* and
// its *version*): listed with their versions and kept current by the leased
// watch; each row opening the document's page, `configuration/<workload>/<document>`
// (DocumentPage.tsx; 01-the-console.md § the address), as text with its
// version beside it, saved with the version as the guard and a conflict shown
// in the person's words; deleted from the row menu with a confirmation;
// *Add settings* a form in the tab's place (§ conventions: a form). The Configuration page draws one per
// workload for an admin; a workload's own page draws it for its registrant,
// through the same verbs. Nothing here validates a document's meaning: beside
// a platform image's settings the field list that image reads is a hint, not
// a schema.

import { identity } from "@impire-io/sdk";
import { Plus } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

import { CODE_NO_SERVICE, isRefusal } from "../../platform/bus.ts";
import { usePage } from "../../platform/session.tsx";
import {
  Button,
  Empty,
  Field,
  NotAllowed,
  Notice,
  Pending,
  Select,
  TextArea,
  TextInput,
} from "../../ui/components.tsx";
import {
  Code,
  DOCUMENT_REQUIRED,
  DOCUMENT_SETTINGS,
  documentOf,
  MAX_DOCUMENT,
  resolve,
  validDocument,
  validDocumentName,
  type Entry,
} from "../../wire/config.ts";
import { SETTINGS_FIELDS } from "../../wire/inference.ts";
import { FormPage, useFormState } from "../../ui/form.tsx";
import { useSurface } from "../surface.tsx";
import { Table, type Column, type MenuEntry } from "../../ui/table.tsx";
import { useManaged } from "../workspaces/managed.ts";
import { documentAddress, EMPTY, fold, sorted, type Back, type Owner, type View } from "./documents.ts";

/** Followed is one workload's documents as the watch last showed them, keyed by the workload so a change of workload reads as empty. */
interface Followed {
  workload: string;
  view: View;
  err: unknown;
}

/** useDocuments follows one workload's documents through the leased watch for as long as the component lives. */
export function useDocuments(workload: string): { view: View; err: unknown } {
  const s = useSurface();
  const [st, setSt] = useState<Followed>({ workload: "", view: EMPTY, err: undefined });
  const bus = s.bus;
  useEffect(() => {
    const ctl = new AbortController();
    const same = (p: Followed) => (p.workload === workload ? p.view : EMPTY);
    void (async () => {
      try {
        for await (const ev of bus.configuration().watch(workload, ctl.signal)) {
          setSt((p) => ({ workload, view: fold(same(p), ev), err: undefined }));
        }
      } catch (e) {
        if (!ctl.signal.aborted) {
          setSt((p) => ({ workload, view: same(p), err: e }));
        }
      }
    })();
    return () => {
      ctl.abort();
    };
  }, [bus, workload]);
  return st.workload === workload ? { view: st.view, err: st.err } : { view: EMPTY, err: undefined };
}

/**
 * useNewer follows the one document an editor holds, through
 * `config.watch.workload.<id>.<document>`, and reports whether a version newer
 * than the one read has landed — saved by someone else while the editor is
 * open — so the editor says so before a save is refused. Nothing is followed
 * for a new document.
 */
function useNewer(workload: string, document: string | undefined, revision: number | undefined): boolean {
  const s = useSurface();
  const bus = s.bus;
  const [newest, setNewest] = useState<{ key: string; revision: number }>({ key: "", revision: 0 });
  const key = `${workload}/${document ?? ""}`;
  useEffect(() => {
    if (document === undefined) {
      return;
    }
    const ctl = new AbortController();
    void (async () => {
      try {
        for await (const ev of bus.configuration().watchDocument(workload, document, ctl.signal)) {
          const r = ev.entry?.revision;
          if (r !== undefined) {
            setNewest((p) => (p.key === key && p.revision >= r ? p : { key, revision: r }));
          }
        }
      } catch {
        // A refused watch leaves the save's own guard to say it.
      }
    })();
    return () => {
      ctl.abort();
    };
  }, [bus, workload, document, key]);
  return revision !== undefined && newest.key === key && newest.revision > revision;
}

/** settingsMenu is a settings document's row menu: *Delete*, asked first. */
export function settingsMenu(name: string, remove: () => void): MenuEntry[] {
  return [
    {
      label: "Delete",
      danger: true,
      confirm: {
        title: `Delete ${name}?`,
        consequence: `Not reversible: ${name} is gone, and whatever reads it finds nothing there.`,
      },
      onSelect: remove,
    },
  ];
}

export function WorkloadSettings({
  workload,
  image,
  canEdit,
  compact,
  head,
  hideWhenEmpty,
  back,
}: {
  workload: string;
  /** The workload's image reference: where it is a platform image, the field list it reads is shown as a hint. */
  image?: string;
  canEdit: boolean;
  /** On a workload's own page, where the section has its own head. */
  compact?: boolean;
  /** When given, the table is drawn in a card under this head. */
  head?: ReactNode;
  /** Draw nothing at all for a workload with no settings: a removed workload on the Configuration page. */
  hideWhenEmpty?: boolean;
  /** The page and tab this table sits on: a document's page opened from a row returns there, its crumb and its delete. */
  back?: Back;
}) {
  const s = useSurface();
  const { platform } = usePage();
  const { view, err } = useDocuments(workload);
  const managed = useManaged();
  const form = useFormState<"add">();
  const [mark, setMark] = useState<string | undefined>(undefined);
  const [actErr, setActErr] = useState<unknown>(undefined);
  const [note, setNote] = useState<string | undefined>(undefined);
  const isModel =
    image !== undefined &&
    image !== "" &&
    image === (platform.images?.[identity.Image.inference] || undefined);
  const docs = sorted(view.docs, DOCUMENT_SETTINGS);
  const noService = isRefusal(err, CODE_NO_SERVICE);
  if (hideWhenEmpty === true && ((view.ready && docs.length === 0) || noService)) {
    return null;
  }

  const remove = (document: string) => {
    setActErr(undefined);
    s.bus
      .configuration()
      .delete(workload, document)
      .then(
        () => {
          setNote(`${document} is gone.`);
        },
        (e: unknown) => {
          setActErr(e);
        },
      );
  };

  const add = canEdit ? (
    <Button
      size={compact === true ? "sm" : undefined}
      onClick={() => {
        form.show("add");
      }}
    >
      <Plus strokeWidth={1.8} aria-hidden="true" />
      Add settings
    </Button>
  ) : undefined;
  // In a managed workspace the required document is the steward's, written
  // from the blueprint: read here, never written (02-DESIGN/13-blueprints/03-required.md § the console).
  const locked = (name: string) => name === DOCUMENT_REQUIRED && managed.managed;
  // One row per document, opening its page; *Delete* in the row
  // menu (01-the-console.md § conventions: a table; a row's actions; remove).
  const columns: Column<Entry>[] = [
    {
      key: "settings",
      header: "Settings",
      width: "minmax(0, 1.6fr)",
      value: (e) => documentOf(e.key),
      cell: (e) => {
        const name = documentOf(e.key);
        return (
          <span className="cell">
            <span className="cell-main mono">{name}</span>
            {isModel && name === DOCUMENT_SETTINGS && (
              <span className="cell-sub">what the model reads: provider, model, name</span>
            )}
            {name === DOCUMENT_REQUIRED && (
              <span className="cell-sub">
                {locked(name) ? `required by ${managed.by}` : "required by this workspace's admins"}: fixes{" "}
                {requiredFields(e.value).join(", ") || "nothing"}; the rest of the settings stay the
                workspace&apos;s
              </span>
            )}
          </span>
        );
      },
    },
    {
      key: "version",
      header: "Version",
      width: "110px",
      value: (e) => e.revision,
      cell: (e) => <span className="muted">{e.revision}</span>,
    },
  ];

  const body = (
    <>
      {actErr !== undefined && <NotAllowed err={actErr} />}
      {note !== undefined && (
        <Notice kind="ok">
          {note}{" "}
          <button
            type="button"
            className="text-link"
            onClick={() => {
              setNote(undefined);
            }}
          >
            Dismiss
          </button>
        </Notice>
      )}
      {noService && (
        <Notice title="No configuration service">
          Nothing serves configuration on this install yet, so there are no settings to show.
        </Notice>
      )}
      {err !== undefined && !noService && <NotAllowed err={err} what="Following the settings" />}
      {err === undefined && !view.ready && <Pending what="Reading the settings" />}
      {view.ready && docs.length === 0 && (
        <Empty action={add}>
          No settings yet. What it reads while it runs goes here, as text, and it sees a change without a
          restart.
        </Empty>
      )}
      {docs.length > 0 && (
        <Table
          label="Settings"
          rows={docs}
          rowKey={(e) => e.key}
          sort={{ key: "settings", dir: "asc" }}
          to={(e) => documentAddress(s.tenant, workload, documentOf(e.key))}
          linkState={back === undefined ? undefined : { back }}
          mark={mark}
          columns={columns}
          menu={(e) => {
            const name = documentOf(e.key);
            return canEdit && !locked(name)
              ? settingsMenu(name, () => {
                  remove(name);
                })
              : [];
          }}
        />
      )}
      {docs.some((e) => documentOf(e.key) === DOCUMENT_REQUIRED) && <ResolvedView docs={docs} />}
      {docs.length > 0 && add !== undefined && (
        <div className="row" style={{ marginTop: 12, justifyContent: "flex-end" }}>
          {add}
        </div>
      )}
    </>
  );
  // *Add settings* replaces what the tab held (01-the-console.md § conventions: a form).
  if (form.open === "add" && canEdit) {
    return (
      <AddDocument
        workload={workload}
        hint={isModel}
        heading="h2"
        back="Settings"
        onClose={form.close}
        onSaved={(e) => {
          form.close();
          setMark(e.key);
          setNote(`Saved ${documentOf(e.key)} as version ${e.revision}.`);
        }}
      />
    );
  }
  if (head === undefined) {
    return body;
  }
  return (
    <section className="section card">
      <div className="card-head">{head}</div>
      {body}
    </section>
  );
}

/** requiredFields are the top-level fields a required document fixes, or none where it is not an object. */
function requiredFields(value: string | undefined): string[] {
  try {
    return resolve("", value ?? "").required;
  } catch {
    return [];
  }
}

/**
 * ResolvedView is what the workload reads: its settings with every field the
 * required document fixes replacing the workload's own, whole — the same
 * resolution every platform image makes (03-required.md § resolution).
 */
function ResolvedView({ docs }: { docs: Entry[] }) {
  const of = (n: string) => docs.find((e) => documentOf(e.key) === n)?.value ?? "";
  let text: string;
  try {
    const r = resolve(of(DOCUMENT_SETTINGS), of(DOCUMENT_REQUIRED));
    text = r.value === "" ? "" : JSON.stringify(JSON.parse(r.value), null, 2);
  } catch {
    text =
      "Its settings and what is required of it cannot be read together: one of them is not a set of fields. The workload refuses to become ready on it.";
  }
  return (
    <details className="section">
      <summary className="small">What it reads: its settings, with what is required over them</summary>
      <pre className="mono small">{text}</pre>
    </details>
  );
}

/**
 * AddDocument is *Add settings*, a form in the page's or the tab's place
 * (01-the-console.md § conventions: a form): it names the document — and,
 * given `owners`, the workload too, which is the Configuration page's add,
 * belonging to no one row — and opens the editor on an empty one. A new
 * document is saved unconditionally, which is what a first write wants.
 */
export function AddDocument({
  workload,
  owners,
  hint,
  hintOf,
  heading,
  back,
  onClose,
  onSaved,
}: {
  workload: string;
  /** The workloads a new document may belong to, picked in the form; the workload is fixed where omitted. */
  owners?: readonly Owner[];
  /** Whether to show the inference image's field list beside the text. */
  hint: boolean;
  /** Whether a workload picked in the form shows the inference image's field list. */
  hintOf?: (workload: string) => boolean;
  /** h1 in the page's place, h2 in a tab's. */
  heading?: "h1" | "h2";
  /** What the form stands in front of, drawn as its crumb. */
  back: string;
  onClose: () => void;
  onSaved: (e: Entry) => void;
}) {
  return (
    <Editor
      workload={workload}
      owners={owners}
      document={undefined}
      entry={undefined}
      canEdit
      hint={hint}
      hintOf={hintOf}
      onSaved={onSaved}
      onReload={() => undefined}
      frame={(body, save) => (
        <FormPage
          title="Add settings"
          line="Text a workload reads while it runs, under a name it asks for."
          back={back}
          heading={heading}
          onClose={onClose}
          foot={
            <>
              <Button kind="quiet" onClick={onClose}>
                Cancel
              </Button>
              {save}
            </>
          }
        >
          {body}
        </FormPage>
      )}
    />
  );
}

/**
 * DocumentEditor is one document as text with its version beside it, the
 * content of the document's page (DocumentPage.tsx). Save names the version
 * it was read at, and a conflict — someone saved a newer version — is shown
 * in those words with a reload, which reads it again.
 */
export function DocumentEditor({
  workload,
  document,
  entry,
  canEdit,
  hint,
  onSaved,
  onReload,
}: {
  workload: string;
  document: string;
  /** The document as read, or nothing where nothing is saved under the name yet. */
  entry: Entry | undefined;
  canEdit: boolean;
  hint: boolean;
  onSaved: (e: Entry) => void;
  onReload: () => void;
}) {
  return (
    <Editor
      workload={workload}
      document={document}
      entry={entry}
      canEdit={canEdit}
      hint={hint}
      onSaved={onSaved}
      onReload={onReload}
      frame={(body, save) => (
        <section className="card">
          <div className="form-body">{body}</div>
          {save !== undefined && <div className="form-foot">{save}</div>}
        </section>
      )}
    />
  );
}

function Editor({
  workload,
  owners,
  document,
  entry,
  canEdit,
  hint,
  hintOf,
  onSaved,
  onReload,
  frame,
}: {
  workload: string;
  owners?: readonly Owner[];
  document: string | undefined;
  entry: Entry | undefined;
  canEdit: boolean;
  hint: boolean;
  hintOf?: (workload: string) => boolean;
  onSaved: (e: Entry) => void;
  onReload: () => void;
  /** Where the editor draws: the fields, and the saving action with the version beside it. */
  frame: (body: ReactNode, save: ReactNode) => ReactNode;
}) {
  const s = useSurface();
  const picking = document === undefined && owners !== undefined;
  const [target, setTarget] = useState(workload !== "" ? workload : (owners?.[0]?.id ?? ""));
  const [name, setName] = useState(document ?? DOCUMENT_SETTINGS);
  const showHint = picking && hintOf !== undefined ? hintOf(target) : hint;
  const [text, setText] = useState(entry?.value ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const [conflict, setConflict] = useState(false);
  const revision = entry?.revision;
  const newer = useNewer(workload, document, revision);
  const size = new TextEncoder().encode(text).length;
  const tooBig = size > MAX_DOCUMENT;
  const nameOK = validDocumentName(name.trim());
  const ready = nameOK && target !== "" && !tooBig && validDocument(text);
  const submit = () => {
    if (busy || !ready || !canEdit) {
      return;
    }
    setErr(undefined);
    setConflict(false);
    setBusy(true);
    s.bus
      .configuration()
      .set(target, name.trim(), text, document === undefined ? undefined : revision)
      .then(
        (e) => {
          setBusy(false);
          onSaved(e);
        },
        (e: unknown) => {
          setBusy(false);
          if (isRefusal(e, Code.conflict)) {
            setConflict(true);
          } else {
            setErr(e);
          }
        },
      );
  };
  const save = canEdit ? (
    <>
      {revision !== undefined && <span className="small faint">version {revision}</span>}
      <Button kind="primary" onClick={submit} disabled={busy || !ready}>
        {busy ? "Saving…" : "Save"}
      </Button>
    </>
  ) : undefined;
  const body = (
    <>
      <p className="muted">
        Text the workload reads while it runs, replaced whole on save. Nothing secret goes here: a secret goes
        in Secrets under the workload&apos;s own path.
      </p>
      {err !== undefined && <NotAllowed err={err} what="Settings" />}
      {(conflict || newer) && (
        <Notice kind="bad" title="Someone saved a newer version">
          Reload to see it; what you typed here is not saved.{" "}
          <button type="button" className="text-link" onClick={onReload}>
            Reload
          </button>
        </Notice>
      )}
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {picking && (
          <Field label="Workload" hint="Whose settings these are: the workload reads them by its own id.">
            <Select
              value={target}
              onChange={setTarget}
              options={owners.map((o) => ({ value: o.id, label: o.name }))}
            />
          </Field>
        )}
        {document === undefined && (
          <Field
            label="Name"
            hint={
              nameOK || name.trim() === ""
                ? "One word, as the workload asks for it; settings is what every platform image reads."
                : "One word: no space, dot, * or > inside it."
            }
          >
            <TextInput value={name} onChange={setName} mono required autoFocus />
          </Field>
        )}
        <Field
          label="Text"
          hint={
            tooBig
              ? `${size} bytes: over the 64 KiB bound.`
              : "JSON by convention, up to 64 KiB. The workload decides what it means."
          }
        >
          <TextArea value={text} onChange={setText} rows={16} mono disabled={!canEdit} />
        </Field>
      </form>
      {showHint && (
        <div className="field">
          <span className="label">What a model reads</span>
          <dl className="fields">
            {SETTINGS_FIELDS.map((f) => (
              <div key={f.field} style={{ display: "contents" }}>
                <dt>{f.field}</dt>
                <dd>{f.what}</dd>
              </div>
            ))}
          </dl>
          <span className="hint">A hint, not a check: the model validates its settings itself.</span>
        </div>
      )}
    </>
  );
  return frame(body, save);
}
